import { AxiosError } from "axios";
import { api } from "@/shared/api/client";
import type {
  ActivityEvent,
  ActivityPeriod,
  BalanceAdjustBody,
  CustomerDetail,
  CustomerListItem,
  CustomerListParams,
  CustomerStatus,
  CustomerUpdate,
  DeveloperToggleBody,
  GrantPremiumBody,
  Paginated,
  TimelineEvent,
} from "./types";
import { ACTIVITY_PAGE_SIZE, PAGE_SIZE } from "./types";
import {
  mockActivity,
  mockAdjustBalance,
  mockDetail,
  mockGrantPremium,
  mockList,
  mockSetDeveloper,
  mockTimeline,
  mockUpdate,
} from "./mock";

// ── Mock toggle ──────────────────────────────────────────────────────────────
// The backend /api/support/customers/* endpoints are built in parallel. Until they
// are live, the UI runs against an in-memory mock that mirrors the exact contract.
// FLIP TO REAL: set USE_MOCK = false (or `VITE_USE_MOCK=false`) — no other change
// needed; the hooks/components already speak the documented shape.
const USE_MOCK =
  (import.meta.env.VITE_USE_MOCK ?? "true").toString().toLowerCase() !== "false";

// Small latency so the loading states are exercised in mock mode.
const delay = (ms = 250) => new Promise<void>((r) => setTimeout(r, ms));

// ── Status casing boundary ───────────────────────────────────────────────────
// The backend serializes User.status as the raw UserStatus value (UPPERCASE:
// NEW / ACTIVE / INACTIVE). The UI uses lowercase. Translate ONLY at this API
// boundary so components/types stay lowercase.
const STATUS_TO_API: Record<CustomerStatus, string> = {
  active: "ACTIVE",
  inactive: "INACTIVE",
  new: "NEW",
};
function statusFromApi(raw: string | null | undefined): CustomerStatus {
  switch ((raw ?? "").toUpperCase()) {
    case "ACTIVE":
      return "active";
    case "NEW":
      return "new";
    default:
      return "inactive";
  }
}
// Raw network shapes — status arrives as an uppercase string, not the UI union.
type RawListItem = Omit<CustomerListItem, "status"> & { status: string };
type RawDetail = Omit<CustomerDetail, "status"> & { status: string };

export async function listCustomers(
  params: CustomerListParams,
): Promise<Paginated<CustomerListItem>> {
  if (USE_MOCK) {
    await delay();
    return mockList(params);
  }
  const res = await api.get<Paginated<RawListItem>>("/support/customers/", {
    params: {
      search: params.search || undefined,
      status: params.status === "all" ? undefined : STATUS_TO_API[params.status],
      page: params.page,
      page_size: PAGE_SIZE,
      // null = backend default (status-first, newest registration).
      ordering: params.ordering || undefined,
    },
  });
  return {
    ...res.data,
    results: res.data.results.map((r) => ({ ...r, status: statusFromApi(r.status) })),
  };
}

export async function getCustomer(id: number): Promise<CustomerDetail> {
  if (USE_MOCK) {
    await delay();
    return mockDetail(id);
  }
  const res = await api.get<RawDetail>(`/support/customers/${id}/`);
  return { ...res.data, status: statusFromApi(res.data.status) };
}

// Read-only lifecycle + support-action feed (newest first). No status casing here:
// timeline status diffs carry the raw backend value as plain text (display-only).
export async function getCustomerTimeline(id: number): Promise<TimelineEvent[]> {
  if (USE_MOCK) {
    await delay();
    return mockTimeline(id);
  }
  const res = await api.get<{ events: TimelineEvent[] }>(
    `/support/customers/${id}/timeline/`,
  );
  return res.data.events;
}

// The customer's own actions (ActionLog, newest first). `last5` is a fixed slice with no
// further pages; `7d` / `all` page by ACTIVITY_PAGE_SIZE.
export async function getCustomerActivity(
  id: number,
  period: ActivityPeriod,
  page: number,
): Promise<Paginated<ActivityEvent>> {
  if (USE_MOCK) {
    await delay();
    return mockActivity(id, period, page);
  }
  const res = await api.get<Paginated<ActivityEvent>>(`/support/customers/${id}/activity/`, {
    params: period === "last5" ? { period } : { period, page, page_size: ACTIVITY_PAGE_SIZE },
  });
  return res.data;
}

export class ConflictError extends Error {
  constructor() {
    super("conflict");
    this.name = "ConflictError";
  }
}

export async function updateCustomer(
  id: number,
  patch: CustomerUpdate,
): Promise<CustomerDetail> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockUpdate(id, patch);
    } catch (e) {
      if ((e as { isConflict?: boolean }).isConflict) throw new ConflictError();
      throw e;
    }
  }
  try {
    // Outgoing status must be the uppercase backend value.
    const body: Record<string, unknown> = { ...patch };
    if (patch.status) body.status = STATUS_TO_API[patch.status];
    const res = await api.patch<RawDetail>(`/support/customers/${id}/`, body);
    return { ...res.data, status: statusFromApi(res.data.status) };
  } catch (e) {
    // 400 = validation/unique conflict → surface a clean inline message upstream.
    if (e instanceof AxiosError && e.response?.status === 400) throw new ConflictError();
    throw e;
  }
}

// ── Sensitive operations (Faza B — 0063.04) ─────────────────────────────────
// Each is a POST to a dedicated @action endpoint, gated by IsSupportOperator and
// audited server-side. A 400 carries a `{ message }` validation error we normalise
// into a stable OpCode so the drawer can show a translated, actionable message.

export type OpCode = "balanceLow" | "alreadyPremium" | "noFreeLeft" | "generic";

export class OpError extends Error {
  constructor(public code: OpCode) {
    super(code);
    this.name = "OpError";
  }
}

// Map a backend validation message (English) to a stable UI code. Kept at this one
// boundary so components never branch on raw server strings.
function opCodeFromMessage(message: string): OpCode {
  const m = message.toLowerCase();
  if (m.includes("balance too low") || m.includes("balance is low")) return "balanceLow";
  if (m.includes("already premium")) return "alreadyPremium";
  if (m.includes("cannot perform free premium")) return "noFreeLeft";
  return "generic";
}

// Unwrap a DRF 400 body ({ message: "…" } | { field: ["…"] }) into one OpError.
function toOpError(e: unknown): OpError {
  // Mock path tags the thrown Error with `opCode`; real path is an AxiosError 400.
  const tagged = (e as { opCode?: OpCode }).opCode;
  if (tagged) return new OpError(tagged);
  if (e instanceof AxiosError && e.response?.status === 400) {
    const data = e.response.data as Record<string, unknown> | undefined;
    let message = "";
    if (data && typeof data.message === "string") {
      message = data.message;
    } else if (data) {
      // Fall back to the first string we can find in a field-errors object.
      const first = Object.values(data).flat().find((v) => typeof v === "string");
      if (typeof first === "string") message = first;
    }
    return new OpError(opCodeFromMessage(message));
  }
  return new OpError("generic");
}

export async function setDeveloper(
  id: number,
  body: DeveloperToggleBody,
): Promise<CustomerDetail> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockSetDeveloper(id, body);
    } catch (e) {
      throw toOpError(e);
    }
  }
  try {
    const res = await api.post<RawDetail>(`/support/customers/${id}/set_developer/`, body);
    return { ...res.data, status: statusFromApi(res.data.status) };
  } catch (e) {
    throw toOpError(e);
  }
}

export async function adjustBalance(
  id: number,
  body: BalanceAdjustBody,
): Promise<CustomerDetail> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockAdjustBalance(id, body);
    } catch (e) {
      throw toOpError(e);
    }
  }
  try {
    const res = await api.post<RawDetail>(`/support/customers/${id}/adjust_balance/`, body);
    return { ...res.data, status: statusFromApi(res.data.status) };
  } catch (e) {
    throw toOpError(e);
  }
}

export async function grantPremium(
  id: number,
  body: GrantPremiumBody,
): Promise<CustomerDetail> {
  if (USE_MOCK) {
    await delay();
    try {
      return mockGrantPremium(id, body);
    } catch (e) {
      throw toOpError(e);
    }
  }
  try {
    const res = await api.post<RawDetail>(`/support/customers/${id}/grant_premium/`, body);
    return { ...res.data, status: statusFromApi(res.data.status) };
  } catch (e) {
    throw toOpError(e);
  }
}
